import {IPartUpdateMetadata, IUserMap, PartUpdatingStatus} from "@buildwithflux/core";
import {IPartData, IReceivingLatestDraftPartsConfig} from "@buildwithflux/models";
import {areWeTestingWithJest, isDevEnv} from "@buildwithflux/shared";
import {StoreApi, UseBoundStore, create} from "zustand";
import {devtools} from "zustand/middleware";
import {useShallow} from "zustand/react/shallow";

import {useFluxServices} from "injection/hooks";
import {FluxLogger} from "modules/storage_engine/connectors/LogConnector";

/**
 * Helper function to get localStorage key for component updates dialog
 */
function getStorageKey(documentUid?: string): string {
    return documentUid ? `lastComponentUpdatesDialogOpenedAt_${documentUid}` : "lastComponentUpdatesDialogOpenedAt";
}

/**
 * State for managing part data and component updates
 *
 * This replaces the Redux part reducer state, which tracked part data,
 * out-of-date parts, part owners, updating status, and component update timestamps.
 */
type PartStoreState = {
    /**
     * The part data associated with the current document if it is published.
     */
    partData: IPartData | undefined;

    /**
     * Metadata about part updates
     */
    partUpdateMetadata: IPartUpdateMetadata[];

    /**
     * Parts that are out of date and need updating
     */
    outOfDateParts: Record<string, IPartUpdateMetadata>;

    /**
     * The owners of the parts in the current document.
     * Maps user UIDs to user data for quick access.
     */
    partOwners: IUserMap;

    /**
     * Map of part UIDs to their updating status
     */
    partUpdatingMap: Record<string, PartUpdatingStatus>;

    /**
     * Timestamps when the component updates dialog was last opened, keyed by document UID
     */
    lastComponentUpdatesDialogOpenedAt: Record<string, number | null>;

    /**
     * Timestamps when the component updates tooltip was last dismissed, keyed by document UID
     */
    lastComponentUpdatesTooltipDismissedAt: Record<string, number | null>;

    /**
     * Sets the part data
     */
    setPartData: (partData: IPartData) => void;

    /**
     * Clears the part data
     */
    clearPartData: () => void;

    /**
     * Acknowledges part updates for the given part UIDs
     */
    acknowledgePartUpdate: (partUids: string[]) => void;

    /**
     * Sets parts as out of date
     */
    setPartsAreOutOfDate: (parts: IPartUpdateMetadata[]) => void;

    /**
     * Sets a single part as up to date
     */
    setPartIsUpToDate: (partUid: string) => void;

    /**
     * Sets multiple parts as up to date
     */
    setPartsAreUpToDate: (partUids: string[]) => void;

    /**
     * Sets the updating status for a part
     */
    setPartUpdating: (partUid: string, updating: PartUpdatingStatus) => void;

    /**
     * Sets the part owners
     */
    setPartOwners: (owners: IUserMap) => void;

    /**
     * Sets the timestamp when the component updates dialog was opened
     */
    setComponentUpdatesDialogOpenedAt: (documentUid: string, timestamp: number) => void;

    /**
     * Sets the timestamp when the component updates tooltip was dismissed
     */
    setComponentUpdatesTooltipDismissedAt: (documentUid: string, timestamp: number) => void;

    /**
     * Resets the store to initial state
     */
    reset: () => void;
};

/**
 * The exported type of the store
 */
export type UsePartStore = UseBoundStore<StoreApi<PartStoreState>>;

/**
 * The exported type of the raw vanilla store (for non-React access like epics)
 */
export type PartStore = StoreApi<PartStoreState>;

const initialState = {
    partData: undefined,
    partUpdateMetadata: [] as IPartUpdateMetadata[],
    outOfDateParts: {} as Record<string, IPartUpdateMetadata>,
    partOwners: {} as IUserMap,
    partUpdatingMap: {} as Record<string, PartUpdatingStatus>,
    lastComponentUpdatesDialogOpenedAt: {} as Record<string, number | null>,
    lastComponentUpdatesTooltipDismissedAt: {} as Record<string, number | null>,
};

/**
 * Creates the vanilla non-bound-to-React part store
 *
 * This is a vanilla Zustand store, and is only exported for use in tests. In components, you'd usually use the hooks
 * below, and even when trying to access low-level details of the store, you'd use the bound version of this store.
 *
 * The store returned from this function cannot be directly used as a hook in a component. For that, see
 * `createPartStoreAndHook` below.
 */
export function createPartStore(): UsePartStore {
    return create<PartStoreState>()(
        devtools(
            (set): PartStoreState => {
                return {
                    ...initialState,

                    setPartData: (partData: IPartData) => {
                        if (!partData) {
                            FluxLogger.captureError(new Error("setPartData called without partData"));
                            return;
                        }
                        set({partData}, false, "setPartData");
                    },

                    clearPartData: () => {
                        set({partData: undefined}, false, "clearPartData");
                    },

                    acknowledgePartUpdate: (partUids: string[]) => {
                        set(
                            (state) => {
                                const newOutOfDateParts = {...state.outOfDateParts};
                                for (const partUid of partUids) {
                                    const outOfDatePart = newOutOfDateParts[partUid];
                                    // NOTE: outOfDatePart might not exist if it is updated to latest
                                    if (outOfDatePart) {
                                        newOutOfDateParts[partUid] = {...outOfDatePart, acknowledged: true};
                                    }
                                }
                                return {outOfDateParts: newOutOfDateParts};
                            },
                            false,
                            "acknowledgePartUpdate",
                        );
                    },

                    setPartsAreOutOfDate: (parts: IPartUpdateMetadata[]) => {
                        set(
                            (state) => {
                                const newOutOfDateParts = {...state.outOfDateParts};
                                for (const part of parts) {
                                    newOutOfDateParts[part.part_uid] = part;
                                }
                                return {outOfDateParts: newOutOfDateParts};
                            },
                            false,
                            "setPartsAreOutOfDate",
                        );
                    },

                    setPartIsUpToDate: (partUid: string) => {
                        set(
                            (state) => {
                                const newOutOfDateParts = {...state.outOfDateParts};
                                delete newOutOfDateParts[partUid];
                                return {outOfDateParts: newOutOfDateParts};
                            },
                            false,
                            "setPartIsUpToDate",
                        );
                    },

                    setPartsAreUpToDate: (partUids: string[]) => {
                        set(
                            (state) => {
                                const newOutOfDateParts = {...state.outOfDateParts};
                                for (const partUid of partUids) {
                                    delete newOutOfDateParts[partUid];
                                }
                                return {outOfDateParts: newOutOfDateParts};
                            },
                            false,
                            "setPartsAreUpToDate",
                        );
                    },

                    setPartUpdating: (partUid: string, updating: PartUpdatingStatus) => {
                        set(
                            (state) => ({
                                partUpdatingMap: {...state.partUpdatingMap, [partUid]: updating},
                            }),
                            false,
                            "setPartUpdating",
                        );
                    },

                    setPartOwners: (owners: IUserMap) => {
                        set({partOwners: owners}, false, "setPartOwners");
                    },

                    setComponentUpdatesDialogOpenedAt: (documentUid: string, timestamp: number) => {
                        set(
                            (state) => {
                                const newLastComponentUpdatesDialogOpenedAt = {
                                    ...state.lastComponentUpdatesDialogOpenedAt,
                                    [documentUid]: timestamp,
                                };
                                // Persist to localStorage
                                if (typeof window !== "undefined") {
                                    const storageKey = getStorageKey(documentUid);
                                    localStorage.setItem(storageKey, String(timestamp));
                                }
                                return {lastComponentUpdatesDialogOpenedAt: newLastComponentUpdatesDialogOpenedAt};
                            },
                            false,
                            "setComponentUpdatesDialogOpenedAt",
                        );
                    },

                    setComponentUpdatesTooltipDismissedAt: (documentUid: string, timestamp: number) => {
                        set(
                            (state) => {
                                const newLastComponentUpdatesTooltipDismissedAt = {
                                    ...state.lastComponentUpdatesTooltipDismissedAt,
                                    [documentUid]: timestamp,
                                };
                                // Persist to localStorage
                                if (typeof window !== "undefined") {
                                    const storageKey = `lastComponentUpdatesTooltipDismissedAt_${documentUid}`;
                                    localStorage.setItem(storageKey, String(timestamp));
                                }
                                return {
                                    lastComponentUpdatesTooltipDismissedAt: newLastComponentUpdatesTooltipDismissedAt,
                                };
                            },
                            false,
                            "setComponentUpdatesTooltipDismissedAt",
                        );
                    },

                    reset: () => {
                        set(initialState, false, "reset");
                    },
                };
            },
            {enabled: isDevEnv() && !areWeTestingWithJest(), name: "PartStore"},
        ),
    );
}

/**
 * Creates both the raw store and the bound hook from the same store instance
 *
 * This is used by the service container to expose both the raw store (for epics) and the hook (for React components)
 */
export function createPartStoreAndHook(): {store: PartStore; hook: UsePartStore} {
    const store = createPartStore();
    return {store, hook: store};
}

// ============================================================================
// React Hooks for accessing part store state
// ============================================================================

/**
 * Gets a reactive slice of the part store: the part data
 * Uses shallow comparison to prevent unnecessary re-renders
 */
export function usePartData(): IPartData | undefined {
    return useFluxServices().usePartStore(useShallow((state) => state.partData));
}

/**
 * Gets a reactive slice of the part store: whether the part is archived
 */
export function useIsPartArchived(): boolean {
    return useFluxServices().usePartStore((state) => (state.partData ? state.partData.archived : false));
}

/**
 * Gets a reactive slice of the part store: the part owners
 */
export function usePartOwners(): IUserMap {
    return useFluxServices().usePartStore(useShallow((state) => state.partOwners));
}

/**
 * Gets a reactive slice of the part store: the out of date parts
 */
export function useOutOfDateParts(): Record<string, IPartUpdateMetadata> {
    return useFluxServices().usePartStore(useShallow((state) => state.outOfDateParts));
}

/**
 * Gets a reactive slice of the part store: the updating status for a specific part
 */
export function usePartUpdatingStatus(partUid: string): PartUpdatingStatus | undefined {
    return useFluxServices().usePartStore((state) => state.partUpdatingMap[partUid]);
}

/**
 * Gets a reactive slice of the part store: the part updating map
 */
export function usePartUpdatingMap(): Record<string, PartUpdatingStatus> {
    return useFluxServices().usePartStore(useShallow((state) => state.partUpdatingMap));
}

/**
 * Gets a reactive slice of the part store: the last component updates dialog opened at timestamp
 */
export function useLastComponentUpdatesDialogOpenedAt(documentUid: string | undefined): number | null {
    return useFluxServices().usePartStore((state) => {
        if (!documentUid) {
            return null;
        }

        if (state.lastComponentUpdatesDialogOpenedAt[documentUid] !== undefined) {
            return state.lastComponentUpdatesDialogOpenedAt[documentUid];
        }

        // Fall back to localStorage
        if (typeof window !== "undefined") {
            const value = localStorage.getItem(`lastComponentUpdatesDialogOpenedAt_${documentUid}`);
            return value ? Number(value) : null;
        }

        return null;
    });
}

/**
 * Gets a reactive slice of the part store: the last component updates tooltip dismissed at timestamp
 */
export function useLastComponentUpdatesTooltipDismissedAt(documentUid: string | undefined): number | null {
    return useFluxServices().usePartStore((state) => {
        if (!documentUid) {
            return null;
        }

        if (state.lastComponentUpdatesTooltipDismissedAt[documentUid] !== undefined) {
            return state.lastComponentUpdatesTooltipDismissedAt[documentUid];
        }

        // Fall back to localStorage
        if (typeof window !== "undefined") {
            const value = localStorage.getItem(`lastComponentUpdatesTooltipDismissedAt_${documentUid}`);
            return value ? Number(value) : null;
        }

        return null;
    });
}

// ============================================================================
// React Hooks for accessing part store actions
// ============================================================================

/**
 * Returns the function to set the part data
 */
export function useSetPartData(): (partData: IPartData) => void {
    return useFluxServices().usePartStore((state) => state.setPartData);
}

/**
 * Returns the function to clear the part data
 */
export function useClearPartData(): () => void {
    return useFluxServices().usePartStore((state) => state.clearPartData);
}

/**
 * Returns the function to acknowledge part updates
 */
export function useAcknowledgePartUpdate(): (partUids: string[]) => void {
    return useFluxServices().usePartStore((state) => state.acknowledgePartUpdate);
}

/**
 * Returns the function to set parts as out of date
 */
export function useSetPartsAreOutOfDate(): (parts: IPartUpdateMetadata[]) => void {
    return useFluxServices().usePartStore((state) => state.setPartsAreOutOfDate);
}

/**
 * Returns the function to set a part as up to date
 */
export function useSetPartIsUpToDate(): (partUid: string) => void {
    return useFluxServices().usePartStore((state) => state.setPartIsUpToDate);
}

/**
 * Returns the function to set multiple parts as up to date
 */
export function useSetPartsAreUpToDate(): (partUids: string[]) => void {
    return useFluxServices().usePartStore((state) => state.setPartsAreUpToDate);
}

/**
 * Returns the function to set the updating status for a part
 */
export function useSetPartUpdating(): (partUid: string, updating: PartUpdatingStatus) => void {
    return useFluxServices().usePartStore((state) => state.setPartUpdating);
}

/**
 * Returns the function to set the part owners
 */
export function useSetPartOwners(): (owners: IUserMap) => void {
    return useFluxServices().usePartStore((state) => state.setPartOwners);
}

/**
 * Returns the function to set the component updates dialog opened at timestamp
 */
export function useSetComponentUpdatesDialogOpenedAt(): (documentUid: string, timestamp: number) => void {
    return useFluxServices().usePartStore((state) => state.setComponentUpdatesDialogOpenedAt);
}

/**
 * Returns the function to set the component updates tooltip dismissed at timestamp
 */
export function useSetComponentUpdatesTooltipDismissedAt(): (documentUid: string, timestamp: number) => void {
    return useFluxServices().usePartStore((state) => state.setComponentUpdatesTooltipDismissedAt);
}

// ============================================================================
// Computed hooks for component updates (replacing Redux selectors)
// ============================================================================

/**
 * Returns the total count of component updates available.
 * Filters out parts that are:
 * - Not in the current document
 * - Already receiving latest drafts
 * - Genesis terminal or route branch point parts
 *
 * @param partUidsInDocument Array of part UIDs that are in the current document
 * @param receivingLatestDrafts Map of part UIDs that are receiving latest drafts
 */
export function useTotalComponentUpdatesCount(
    partUidsInDocument: string[],
    receivingLatestDrafts: IReceivingLatestDraftPartsConfig | undefined,
): number {
    // Import constants inline to avoid circular dependencies
    // eslint-disable-next-line @typescript-eslint/no-require-imports
    const {GENESIS_TERMINAL_PART_UID, ROUTE_BRANCH_POINT_PART_UID} = require("@buildwithflux/core");

    return useFluxServices().usePartStore((state) => {
        const outOfDateParts = state.outOfDateParts;
        if (!outOfDateParts || Object.keys(outOfDateParts).length === 0) {
            return 0;
        }

        const partUidsSet = new Set(partUidsInDocument);
        const outOfDatePartsList = Object.values(outOfDateParts);
        return outOfDatePartsList.filter((part) => {
            if (!partUidsSet.has(part.part_uid)) {
                return false;
            }
            if (receivingLatestDrafts && receivingLatestDrafts[part.part_uid]) {
                return false;
            }
            if (part.part_uid === GENESIS_TERMINAL_PART_UID || part.part_uid === ROUTE_BRANCH_POINT_PART_UID) {
                return false;
            }
            return true;
        }).length;
    });
}

/**
 * Returns the count of new (unacknowledged) component updates since the dialog was last opened.
 *
 * @param partUidsInDocument Array of part UIDs that are in the current document
 * @param lastDialogOpenedAt Timestamp when the dialog was last opened
 */
export function useNewComponentUpdatesCount(partUidsInDocument: string[], lastDialogOpenedAt: number | null): number {
    return useFluxServices().usePartStore((state) => {
        const outOfDateParts = state.outOfDateParts;
        if (!outOfDateParts || Object.keys(outOfDateParts).length === 0) {
            return 0;
        }

        const partUidsSet = new Set(partUidsInDocument);
        const outOfDatePartsList = Object.values(outOfDateParts);
        const relevantParts = outOfDatePartsList.filter((part) => partUidsSet.has(part.part_uid));

        if (lastDialogOpenedAt === null) {
            return relevantParts.filter((part) => !part.acknowledged).length;
        }

        return relevantParts.filter((part) => {
            const partUpdatedAt = new Date(part.updated_at).getTime();
            return !part.acknowledged && partUpdatedAt > lastDialogOpenedAt;
        }).length;
    });
}

/**
 * Returns whether there are new component updates available.
 *
 * @param partUidsInDocument Array of part UIDs that are in the current document
 * @param lastDialogOpenedAt Timestamp when the dialog was last opened
 */
export function useHasNewComponentUpdates(partUidsInDocument: string[], lastDialogOpenedAt: number | null): boolean {
    return useNewComponentUpdatesCount(partUidsInDocument, lastDialogOpenedAt) > 0;
}

/**
 * Returns whether the component updates tooltip should be shown.
 * Shows tooltip if there are unacknowledged parts that were updated after the tooltip was last dismissed.
 *
 * @param partUidsInDocument Array of part UIDs that are in the current document
 * @param lastTooltipDismissedAt Timestamp when the tooltip was last dismissed
 */
export function useShouldShowComponentUpdatesTooltip(
    partUidsInDocument: string[],
    lastTooltipDismissedAt: number | null,
): boolean {
    return useFluxServices().usePartStore((state) => {
        const outOfDateParts = state.outOfDateParts;
        if (!outOfDateParts || Object.keys(outOfDateParts).length === 0) {
            return false;
        }

        const partUidsSet = new Set(partUidsInDocument);
        const outOfDatePartsList = Object.values(outOfDateParts);
        const relevantParts = outOfDatePartsList.filter((part) => partUidsSet.has(part.part_uid));
        const unacknowledgedParts = relevantParts.filter((part) => !part.acknowledged);

        if (unacknowledgedParts.length === 0) {
            return false;
        }

        if (lastTooltipDismissedAt === null) {
            return true;
        }

        return unacknowledgedParts.some((part) => {
            const partUpdatedAt = new Date(part.updated_at).getTime();
            return partUpdatedAt > lastTooltipDismissedAt;
        });
    });
}
